import { Checkbox, FormControlLabel, RadioGroup } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { useTranslator } from '@/hooks/useTranslator';
import { isArrayOf, isString } from '@/types/common-type-guards';
import { SelectItem } from '@/types/fields';

interface Props {
  readonly id?: string;
  readonly name?: string;
  readonly items: readonly SelectItem[];
  readonly defaultValue?: unknown;
}

export const MultipleValuesFormFieldInput: React.FC<Props> = (
  props: React.PropsWithChildren<Props>,
): React.ReactElement => {
  const tr = useTranslator();

  const { id, name, items, defaultValue = [] } = props;

  if (defaultValue !== '' && !isArrayOf<string>(defaultValue, isString)) {
    throw new Error('`defaultValue` must be an array of strings');
  }

  if (items.some((item: SelectItem): boolean => item.value === 'none')) {
    throw new Error('`none` must be uppercase');
  }

  const [values, setValues] = React.useState<Record<string, boolean>>(
    transformDefaultValue(defaultValue),
  );

  const changeHandler = React.useCallback(
    (item: SelectItem): React.ChangeEventHandler<HTMLInputElement> | undefined => {
      return (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { currentTarget } = event;

        switch (currentTarget.value) {
          case 'NONE':
            setValues({ NONE: true });
            break;
          case 'other':
            // TODO: maybe add an "what is other" field
            break;
          default:
            setValues((values: Record<string, boolean>): Record<string, boolean> => {
              return { ...values, [item.value]: currentTarget.checked, NONE: false };
            });
            break;
        }
      };
    },
    [],
  );

  return (
    <RadioGroup id={id}>
      <Box sx={styles.container}>
        {items.map((item: SelectItem): React.ReactElement => {
          return (
            <Box key={item.value} width="100%">
              <FormControlLabel
                sx={styles.control}
                control={
                  <Box ml={-1}>
                    <Checkbox
                      name={name}
                      value={item.value}
                      checked={Boolean(values[item.value])}
                      onChange={changeHandler(item)}
                    />
                  </Box>
                }
                label={
                  <Typography fontSize={16} whiteSpace="nowrap" color="text.primary">
                    {tr(item.label)}
                  </Typography>
                }
              />
            </Box>
          );
        })}
      </Box>
    </RadioGroup>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'grid',
    columnGap: 3,
    gridTemplateColumns: 'repeat(2, 248px)',
  },
  control: {
    display: 'flex',
    alignItems: 'center',
    margin: 0,
  },
};

const transformDefaultValue = (defaultValue: readonly string[] | ''): Record<string, boolean> => {
  if (defaultValue === '') {
    return {};
  }

  return defaultValue.reduce(
    (mapped: Record<string, boolean>, value: string): Record<string, boolean> => ({
      ...mapped,
      [value]: true,
    }),
    {},
  );
};
